/**
 * @author      Dario Casertano <dario@casertano.name>
 * @copyright   Copyright (c) 2023 Casertano Dario – All rights reserved.
 * @license     Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International.
 */

import { settings } from "@/libraries/storage";
import { VuetifyThemeVariant } from "vuetify/types/services/theme";
import { Module, Mutation, VuexModule } from "vuex-module-decorators";

const darkTheme = settings.get<Partial<VuetifyThemeVariant> | null>('ux', 'theme', 'dark')
const lightTheme = settings.get<Partial<VuetifyThemeVariant> | null>('ux', 'theme', 'light')

@Module({
    name: 'VrStore',
    namespaced: true,
})
export default class VrStore extends VuexModule {
    hasDarkMode = (darkTheme !== null)
    hasLightMode = (lightTheme !== null)

    darkMode = settings.get<boolean>('ux', 'theme', 'darkMode', false)
    darkTheme = darkTheme
    lightTheme = lightTheme
    longName = settings.get<string>('ux', 'theme', 'longName', '...')
    shortName = settings.get<string>('ux', 'theme', 'shortName', '...')

    @Mutation
    setDarkMode(darkMode: boolean): void {
        settings.set<boolean>('ux', 'theme', 'darkMode', darkMode)

        this.darkMode = darkMode
    }

    @Mutation
    setLongName(longName: string): void {
        settings.set<string>('ux', 'theme', 'longName', longName)

        this.longName = longName
    }

    @Mutation
    setShortName(shortName: string): void {
        settings.set<string>('ux', 'theme', 'shortName', shortName)

        this.shortName = shortName
    }

    @Mutation
    setTheme(theme: {
        darkTheme: Partial<VuetifyThemeVariant> | null
        lightTheme: Partial<VuetifyThemeVariant> | null
    }): void {
        const {
            darkTheme,
            lightTheme,
        } = theme

        settings.set<Partial<VuetifyThemeVariant> | null>('ux', 'theme', 'dark', darkTheme)
        settings.set<Partial<VuetifyThemeVariant> | null>('ux', 'theme', 'light', lightTheme)

        this.darkTheme = darkTheme
        this.lightTheme = lightTheme

        this.hasDarkMode = (darkTheme !== null)
        this.hasLightMode = (lightTheme !== null)

        let darkMode: boolean

        if (this.hasDarkMode && this.hasLightMode) {
            darkMode = settings.get<boolean>('ux', 'theme', 'darkMode',
                (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches))
        } else {
            darkMode = this.hasDarkMode
        }

        settings.set<boolean>('ux', 'theme', 'darkMode', darkMode)

        this.darkMode = darkMode
    }
}
